import axios from "axios";
import store from "@/store";
import { installWikiErrorLocalization } from "@/services/wiki/client-localization";

const xunzhiKbClient = axios.create({
  baseURL: "/wiki-api",
  timeout: 60000,
});
installWikiErrorLocalization(xunzhiKbClient, () => store.state.locale?.uiLocale || "zh-CN");

const withUserAuth = (config = {}) => {
  const token = store.getters.token;
  return {
    ...config,
    headers: token ? { ...(config.headers || {}), Authorization: token } : (config.headers || {}),
  };
};

export function searchKbProducts(params = {}) {
  return xunzhiKbClient.get("/api/ai/search", { params });
}

export function listKbProducts(params = {}) {
  return xunzhiKbClient.get("/api/ai/products", { params });
}

// 供需详情复用百科受控预览，登录凭证随请求发送，不暴露 OSS 源地址。
export function getKbProductPdf(productId) {
  return xunzhiKbClient.get(
    `/api/documents/pdf-preview/product/${encodeURIComponent(productId)}`,
    withUserAuth({ responseType: "blob" }),
  );
}

export function searchStandardAnswers(params = {}) {
  return xunzhiKbClient.get("/api/ai/standard-answers", { params });
}

export function listKbManufacturers(params = {}) {
  return xunzhiKbClient.get("/api/ai/manufacturers", { params });
}

export function listKbProductCategories(params = {}) {
  return xunzhiKbClient.get("/api/ai/product-categories", { params, timeout: 130000 });
}

export function getWikiCatalogReleaseFeed() {
  return xunzhiKbClient.get("/api/ai/wiki-catalog/release-feed", {
    timeout: 10000,
    headers: { "Cache-Control": "no-cache" },
  });
}

export function listWikiEntries(params = {}) {
  const config = params.public_only ? { params } : withUserAuth({ params });
  return xunzhiKbClient.get("/api/ai/wiki-entries", config);
}

export function createWikiEntry(data = {}) {
  return xunzhiKbClient.post("/api/ai/wiki-entries", data, withUserAuth({ timeout: 130000 }));
}

export function getWikiEntryHistory(id, params = {}) {
  return xunzhiKbClient.get(
    `/api/ai/wiki-entries/${encodeURIComponent(id)}/history`,
    { params },
  );
}

export function uploadWikiEntryDatasheet(file) {
  const formData = new FormData();
  formData.append("file", file);
  return xunzhiKbClient.post(
    "/api/ai/wiki-entries/datasheet",
    formData,
    withUserAuth({ timeout: 130000 }),
  );
}

export function listMyWikiEntries() {
  return xunzhiKbClient.get("/api/ai/wiki-entries/mine", withUserAuth());
}

export function updateWikiEntry(id, data = {}) {
  return xunzhiKbClient.patch(
    `/api/ai/wiki-entries/${encodeURIComponent(id)}`,
    data,
    withUserAuth({ timeout: 130000 }),
  );
}

export function publishWikiEntryNow(id) {
  return xunzhiKbClient.post(
    `/api/ai/wiki-entries/${encodeURIComponent(id)}/publish`,
    {},
    withUserAuth({ timeout: 130000 }),
  );
}

export function withdrawWikiEntry(id) {
  return xunzhiKbClient.post(
    `/api/ai/wiki-entries/${encodeURIComponent(id)}/withdraw`,
    {},
    withUserAuth(),
  );
}

export function deleteWikiDraft(id) {
  return xunzhiKbClient.delete(
    `/api/ai/wiki-entries/${encodeURIComponent(id)}`,
    withUserAuth(),
  );
}

export function getWikiEntry(id) {
  return xunzhiKbClient.get(`/api/ai/wiki-entries/${encodeURIComponent(id)}`, withUserAuth());
}

export function listMyFavoriteWikiEntries() {
  return xunzhiKbClient.get("/api/ai/wiki-entries/favorites/mine", withUserAuth());
}

export function favoriteWikiEntry(id) {
  return xunzhiKbClient.post(
    `/api/ai/wiki-entries/${encodeURIComponent(id)}/favorite`,
    {},
    withUserAuth(),
  );
}

export function unfavoriteWikiEntry(id) {
  return xunzhiKbClient.delete(
    `/api/ai/wiki-entries/${encodeURIComponent(id)}/favorite`,
    withUserAuth(),
  );
}

export function getKbProductDetail(id) {
  const standard = /^standard-([1-9]\d*)$/.exec(String(id));
  if (standard) return xunzhiKbClient.get(`/api/ai/standard-answers/${standard[1]}`);
  return xunzhiKbClient.get(`/api/ai/products/${id}`);
}

export function getKbProductTextAsset(id) {
  return xunzhiKbClient.get(`/api/ai/products/${id}/text-asset`, {
    timeout: 130000,
  });
}

export function getKbProductDatasheetSummary(id, params = {}) {
  return xunzhiKbClient.get(`/api/ai/products/${id}/datasheet-summary`, {
    params,
    timeout: 130000,
  });
}

export function getKbPublishedEncyclopediaContent(id) {
  return xunzhiKbClient.get(`/api/ai/products/${id}/encyclopedia-content`);
}

export function getKbEncyclopediaContent(contentId) {
  return xunzhiKbClient.get(
    `/api/ai/encyclopedia-contents/${encodeURIComponent(contentId)}`,
    withUserAuth(),
  );
}

export function getKbProductEncyclopediaHistory(id, params = {}) {
  return xunzhiKbClient.get(
    `/api/ai/products/${encodeURIComponent(id)}/encyclopedia-content/history`,
    { params },
  );
}

export function generateKbEncyclopediaDraft(id, data = {}) {
  return xunzhiKbClient.post(`/api/ai/products/${id}/encyclopedia-content/generate`, data, withUserAuth({
    timeout: 130000,
  }));
}

export function publishKbEncyclopediaContent(contentId, data = {}) {
  return xunzhiKbClient.post(`/api/ai/encyclopedia-contents/${contentId}/publish`, data, withUserAuth());
}

export function scheduleKbEncyclopediaContent(contentId, data = {}) {
  return xunzhiKbClient.post(`/api/ai/encyclopedia-contents/${contentId}/schedule`, data, withUserAuth());
}

export function listKbProductEncyclopediaDrafts(id, params = {}) {
  return xunzhiKbClient.get(
    `/api/ai/products/${encodeURIComponent(id)}/encyclopedia-content/drafts`,
    withUserAuth({ params }),
  );
}

export function updateKbEncyclopediaContent(contentId, data = {}) {
  return xunzhiKbClient.patch(`/api/ai/encyclopedia-contents/${contentId}`, data, withUserAuth());
}

export function submitKbEncyclopediaContentForReview(contentId, data = {}) {
  return xunzhiKbClient.post(`/api/ai/encyclopedia-contents/${contentId}/submit-review`, data);
}

export function cloneKbEncyclopediaContent(contentId) {
  return xunzhiKbClient.post(`/api/ai/encyclopedia-contents/${contentId}/clone`, {}, withUserAuth());
}

export function confirmKbEncyclopediaAiCorrections(contentId) {
  return xunzhiKbClient.post(`/api/ai/encyclopedia-contents/${contentId}/ai-corrections/confirm`, {}, withUserAuth({ timeout: 130000 }));
}

export function rejectKbEncyclopediaAiCorrections(contentId) {
  return xunzhiKbClient.post(`/api/ai/encyclopedia-contents/${contentId}/ai-corrections/reject`, {}, withUserAuth());
}

export function confirmWikiEntryAiCorrections(id) {
  return xunzhiKbClient.post(`/api/ai/wiki-entries/${encodeURIComponent(id)}/ai-corrections/confirm`, {}, withUserAuth({ timeout: 130000 }));
}

export function rejectWikiEntryAiCorrections(id) {
  return xunzhiKbClient.post(`/api/ai/wiki-entries/${encodeURIComponent(id)}/ai-corrections/reject`, {}, withUserAuth());
}

export function getWikiReviewAccess() {
  return xunzhiKbClient.get("/api/ai/wiki-review/access", withUserAuth());
}

export function listWikiReviewQueue(params = {}) {
  return xunzhiKbClient.get("/api/ai/wiki-review/queue", {
    ...withUserAuth(),
    params,
  });
}

export function listWikiManagementEntries(params = {}) {
  return xunzhiKbClient.get("/api/ai/wiki-review/entries", {
    ...withUserAuth(),
    params,
  });
}

export function reviewWikiEntry(id, data = {}) {
  return xunzhiKbClient.post(
    `/api/ai/wiki-entries/${encodeURIComponent(id)}/manual-review`,
    data,
    withUserAuth({ timeout: 130000 }),
  );
}

export function reviewKbEncyclopediaContent(contentId, data = {}) {
  return xunzhiKbClient.post(
    `/api/ai/encyclopedia-contents/${encodeURIComponent(contentId)}/manual-review`,
    data,
    withUserAuth({ timeout: 130000 }),
  );
}

export function unpublishWikiEntry(id, data = {}) {
  return xunzhiKbClient.post(
    `/api/ai/wiki-entries/${encodeURIComponent(id)}/unpublish`,
    data,
    withUserAuth(),
  );
}

export function reopenWikiEntryReview(id) {
  return xunzhiKbClient.post(
    `/api/ai/wiki-entries/${encodeURIComponent(id)}/reopen-review`,
    {},
    withUserAuth(),
  );
}

export function unpublishKbEncyclopediaContent(contentId, data = {}) {
  return xunzhiKbClient.post(
    `/api/ai/encyclopedia-contents/${encodeURIComponent(contentId)}/unpublish`,
    data,
    withUserAuth(),
  );
}

export function reopenKbEncyclopediaReview(contentId) {
  return xunzhiKbClient.post(
    `/api/ai/encyclopedia-contents/${encodeURIComponent(contentId)}/reopen-review`,
    {},
    withUserAuth(),
  );
}

// 百科积分属于知识库服务，需携带商城登录态以解析当前用户。
export function getKbPointsBalance() {
  return xunzhiKbClient.get("/api/documents/points/balance", withUserAuth());
}

export function listKbPointsLedger(params = {}) {
  return xunzhiKbClient.get("/api/documents/points/ledger", {
    ...withUserAuth(),
    params,
  });
}

export function getKbDatasheetDownloadQuote(productId) {
  return xunzhiKbClient.get(
    `/api/documents/points/download-quote/${encodeURIComponent(productId)}`,
    withUserAuth(),
  );
}

// 当前登录用户已购买（下载）的百科规格书。
export function listKbDownloadedDatasheets(params = {}) {
  return xunzhiKbClient.get("/api/documents/points/purchases", {
    ...withUserAuth(),
    params,
  });
}

// EventSource does not use Axios. Keep this URL relative so the existing
// development/production proxy routes it to the standalone wiki backend.
export function getKbRealtimeVisualStreamUrl(id) {
  return `/wiki-api/api/realtime-visual/products/${encodeURIComponent(id)}/stream`;
}

export function askKbAssistant(data = {}, config = {}) {
  return xunzhiKbClient.post("/api/ai/assistant/chat", data, { timeout: 150000, ...config });
}
